/**
 * VendedorListasPage - Espejo exacto del POS
 *
 * Columnas dinámicas según tipo de sorteo:
 * - NORMAL: # | MONTO
 * - REVENTADO: # | REV1 | REV2
 * - MEGA: # | NUM2 | REV1 | REV2 | MEGA
 * - MONAZOS: NUM | TIPO | MONTO
 * - FECHAS: FECHA | MONTO
 * - PARLEY: NUM1 | NUM2 | MONTO
 *
 * Estilo: Excel, plano, rápido
 * NO muestra: vendido, disponible, cálculos, totales
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { DynamicListTable, DynamicColumn } from '../../../components/DynamicListTable';
import { NumerosGrid, TipoNumerosGrid } from '../../../components/NumerosGrid';
import { fetchAPI } from '../../../services/apiClient';
import { fechaLocalYMD } from '../../../utils/date';
import { onVenta, VentaEvento } from '../../../utils/listasBus';

interface Sorteo {
  id: string;
  tipo_id: string;
  nombre: string;
  fecha: string;
  hora_apertura?: string;
  hora_cierre?: string;
  estado: string;
}

const formatHora = (hora?: string): string => {
  if (!hora) return '';
  return hora.length >= 5 ? hora.slice(0, 5) : hora;
};

const sorteoLabel = (s: Sorteo): string => {
  const hora = formatHora(s.hora_cierre);
  const hoy = fechaLocalYMD();
  return hora
    ? `${s.nombre} (${s.tipo_id}) • ${hoy} ${hora}`
    : `${s.nombre} (${s.tipo_id}) • ${hoy}`;
};

const formatMontoSinCentavos = (value: unknown): string => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '0';
  return Math.trunc(n).toLocaleString('es-CR');
};

const mapRowMontosSinCentavos = (row: any, keys: string[]): any => {
  const out = { ...row };
  for (const k of keys) {
    if (k in out) out[k] = formatMontoSinCentavos(out[k]);
  }
  return out;
};

// Clave única por fila para diff diferencial
const rowKey = (item: any): string => {
  if (item.id) return String(item.id);
  const parts = [item.numero, item.num1, item.num2, item.tipo].filter((v): v is string => v != null);
  return parts.length > 0 ? parts.join('-') : '_row';
};

// Comparación campo a campo (excluye _highlight)
const hasChanged = (anterior: any, nuevo: any): boolean => {
  for (const k of Object.keys(nuevo)) {
    if (k === '_highlight') continue;
    if (anterior[k] !== nuevo[k]) return true;
  }
  return false;
};

/**
 * Aplica jugadas de una venta recién confirmada directamente al estado local
 * en modo optimista. Solo se usa para tipos con lógica agregada simple.
 * El polling reconciliará con los valores reales del backend.
 */
const aplicarOptimista = (prev: any[], evento: VentaEvento): any[] => {
  if (prev.length === 0) return prev;

  // Para tipos NumerosGrid (NORMAL, REVENTADO): actualiza la fila por numero
  if (evento.tipo_id === 'NORMAL') {
    const mapa = new Map(prev.map(r => [r.numero, r]));
    let cambio = false;
    for (const j of evento.jugadas) {
      const key = String(j.num1).padStart(2, '0');
      const fila = mapa.get(key);
      if (fila) {
        mapa.set(key, { ...fila, vendido: (Number(fila.vendido) || 0) + j.monto, _optimistic: true });
        cambio = true;
      }
    }
    return cambio ? Array.from(mapa.values()) : prev;
  }

  if (evento.tipo_id === 'REVENTADO') {
    const mapa = new Map(prev.map(r => [r.numero, r]));
    let cambio = false;
    for (const j of evento.jugadas) {
      const key = String(j.num1).padStart(2, '0');
      const fila = mapa.get(key);
      if (fila) {
        // num2 = '1' → rev1, '2' → rev2; si no, aplica a rev1 por defecto
        const esRev2 = j.num2 === '2';
        mapa.set(key, esRev2
          ? { ...fila, vendido_rev2: (Number(fila.vendido_rev2) || 0) + j.monto, _optimistic: true }
          : { ...fila, vendido_rev1: (Number(fila.vendido_rev1) || 0) + j.monto, _optimistic: true }
        );
        cambio = true;
      }
    }
    return cambio ? Array.from(mapa.values()) : prev;
  }

  // Para otros tipos: no podemos estimar sin más contexto → dejar lista intacta,
  // el refresh inmediato se encargará de actualizar en ~200ms
  return prev;
};

// NormalFiveColumnTable y ReventadoFiveColumnTable reemplazadas por NumerosGrid

// Definición de columnas por tipo
const getColumnsForTipo = (tipoId: string): DynamicColumn[] => {
  switch (tipoId) {
    case 'NORMAL':
      return [
        { key: 'numero', label: '#', type: 'readonly', width: '80px' },
        { key: 'monto', label: 'MONTO', type: 'readonly', width: '150px' }
      ];

    case 'REVENTADO':
      return [
        { key: 'numero', label: '#', type: 'readonly', width: '80px' },
        { key: 'vendido_rev1', label: 'REV1', type: 'readonly', width: '150px' },
        { key: 'vendido_rev2', label: 'REV2', type: 'readonly', width: '150px' }
      ];

    case 'MEGA':
      return [
        { key: 'numero', label: '#', type: 'readonly', width: '80px' },
        { key: 'rev1', label: 'REV1', type: 'readonly', width: '110px' },
        { key: 'rev2', label: 'REV2', type: 'readonly', width: '110px' },
        { key: 'num2', label: 'NúM2', type: 'readonly', width: '70px' },
        { key: 'monto_mega', label: 'MONTO', type: 'readonly', width: '110px' }
      ];

    case 'MONAZOS':
      return [
        { key: 'numero', label: 'NUM', type: 'readonly', width: '100px' },
        { key: 'tipo', label: 'TIPO', type: 'readonly', width: '120px' },
        { key: 'vendido', label: 'MONTO', type: 'readonly', width: '150px' }
      ];

    case 'FECHAS':
      return [
        { key: 'numero', label: 'FECHA', type: 'readonly', width: '120px' },
        { key: 'vendido', label: 'MONTO', type: 'readonly', width: '150px' }
      ];

    case 'PARLEY':
      return [
        { key: 'num1', label: 'NUM1', type: 'readonly', width: '100px' },
        { key: 'num2', label: 'NUM2', type: 'readonly', width: '100px' },
        { key: 'vendido', label: 'MONTO', type: 'readonly', width: '150px' }
      ];

    default:
      return [
        { key: 'numero', label: '#', type: 'readonly', width: '80px' },
        { key: 'vendido', label: 'MONTO', type: 'readonly', width: '150px' }
      ];
  }
};

export const VendedorListasPage = () => {
  const [sorteos, setSorteos] = useState<Sorteo[]>([]);
  const [selectedSorteo, setSelectedSorteo] = useState<Sorteo | null>(null);
  const [lista, setLista] = useState<any[]>([]);
  const [restringidosSet, setRestringidosSet] = useState<Set<string>>(new Set());
  const [topSet, setTopSet] = useState<Set<string>>(new Set());
  const [loadingInicial, setLoadingInicial] = useState(false);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);



  const cargarSorteos = () => {
    const hoy = fechaLocalYMD();
    fetchAPI<Sorteo[]>(`/sorteos?fecha=${hoy}`)
      .then(data => {
        setSorteos(data);
        setSelectedSorteo(prev => {
          if (!data.length) return null;
          if (!prev) return data[0];
          const match = data.find(x => x.id === prev.id);
          return match || data[0];
        });
      })
      .catch(err => console.error('Error cargando sorteos:', err));
  };

  // Cargar sorteos abiertos y refrescar en caliente
  useEffect(() => {
    cargarSorteos();
    const interval = window.setInterval(cargarSorteos, 15000);
    const onFocus = () => cargarSorteos();
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  // Cargar lista cuando cambia sorteo
  const cargarLista = (sorteo: Sorteo, silencioso = false) => {
    if (!silencioso) {
      setLoadingInicial(true);
    }
    const tipo = sorteo.tipo_id;
    const sorteoId = sorteo.id;
    const hoy = fechaLocalYMD();
    const endpoint = ['NORMAL', 'MEGA'].includes(tipo)
      ? `/listas/individuales/${sorteoId}?fecha=${hoy}`
      : `/listas/consolidada/${sorteoId}?fecha=${hoy}`;

    fetchAPI<any[]>(endpoint)
      .then(data => {
        const montoKeysByTipo: Record<string, string[]> = {
          NORMAL: ['monto'],
          REVENTADO: ['vendido_rev1', 'vendido_rev2'],
          MEGA: ['rev1', 'rev2', 'monto_mega'],
          MONAZOS: ['vendido'],
          FECHAS: ['vendido'],
          PARLEY: ['vendido'],
        };
        // Calcular nueva lista según tipo
        let nextLista: any[];
        if (tipo === 'REVENTADO') {
          nextLista = Array.isArray(data) ? data : [];
        } else if (tipo === 'NORMAL') {
          // Agregar por número para la grilla de 5 columnas: {numero, vendido}
          const agr: Record<string, number> = {};
          for (const r of Array.isArray(data) ? data : []) {
            const n = String(r.numero).padStart(2, '0');
            agr[n] = (agr[n] || 0) + (Number(r.monto) || 0);
          }
          nextLista = Object.entries(agr).map(([numero, vendido]) => ({ numero, vendido }));
        } else {
          const keys = montoKeysByTipo[tipo] ?? ['vendido'];
          nextLista = Array.isArray(data) ? data.map((r: any) => mapRowMontosSinCentavos(r, keys)) : [];
        }
        if (!silencioso) {
          // Carga inicial: establecer directamente sin diff
          setLista(nextLista);
          setLoadingInicial(false);
        } else {
          // Polling: actualización diferencial fila a fila, sin parpadeo
          setLista(prev => {
            const prevMap = new Map(prev.map(item => [rowKey(item), item]));
            let cambio = false;
            const resultado: any[] = [];

            for (const nuevo of nextLista) {
              const key = rowKey(nuevo);
              const anterior = prevMap.get(key);
              if (anterior && !hasChanged(anterior, nuevo)) {
                // Sin cambios: reutilizar misma referencia de objeto
                resultado.push(anterior._highlight ? { ...anterior, _highlight: false } : anterior);
              } else {
                // Nuevo o modificado: nueva referencia con highlight amarillo
                resultado.push({ ...nuevo, _highlight: true });
                cambio = true;
              }
            }

            // Sin diferencias y mismo tamaño: no actualizar estado
            if (!cambio && resultado.length === prev.length) return prev;

            // Apagar highlight después de 300ms (no-op si no hay highlights)
            setTimeout(() => {
              setLista(current => {
                if (!current.some(r => r._highlight)) return current;
                return current.map(r => r._highlight ? { ...r, _highlight: false } : r);
              });
            }, 300);

            return resultado;
          });
        }
      })
      .catch(err => {
        console.error('Error cargando lista:', err);
        if (!silencioso) {
          setLoadingInicial(false);
        }
      });
  };

  useEffect(() => {
    if (!selectedSorteo) return;
    cargarLista(selectedSorteo, false);

    const interval = window.setInterval(() => {
      cargarLista(selectedSorteo, true);
    }, 5000);
    const onFocus = () => cargarLista(selectedSorteo, true);
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [selectedSorteo]);

  // Cargar top-vendidos y restringidos cuando cambia el sorteo
  useEffect(() => {
    if (!selectedSorteo) {
      setRestringidosSet(new Set());
      setTopSet(new Set());
      return;
    }
    const cargarTopRestringidos = () => {
      fetchAPI<{ top: Array<{ numero: string; total: number }>; restringidos: string[] }>(
        `/listas/top-vendidos/${selectedSorteo.id}`
      )
        .then(data => {
          setRestringidosSet(new Set(data.restringidos || []));
          setTopSet(new Set((data.top || []).map((t: { numero: string }) => t.numero)));
        })
        .catch(() => { /* silencioso: no afectar la lista */ });
    };
    cargarTopRestringidos();
    const iv = window.setInterval(cargarTopRestringidos, 30000);
    return () => window.clearInterval(iv);
  }, [selectedSorteo?.id]);

  // Suscripción al bus de ventas: refresh optimista inmediato al confirmar venta
  useEffect(() => {
    return onVenta(evento => {
      if (!selectedSorteo || evento.sorteo_id !== selectedSorteo.id) return;

      // 1) Aplicar cambio optimista instantáneo en UI
      setLista(prev => aplicarOptimista(prev, evento));

      // 2) Debounce refresh: cancelar timer previo para no acumular renders
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(() => cargarLista(selectedSorteo, true), 250);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSorteo]);

  // Listas son de solo lectura (ligadas a tiquetes). No se permite editar.
  const handleSave = useCallback(async (_rowId: number, _field: string, _value: any) => { }, []);



  const columns = useMemo(
    () => (selectedSorteo ? getColumnsForTipo(selectedSorteo.tipo_id) : []),
    [selectedSorteo?.tipo_id]
  );

  // Lista con flags de restringido/top inyectados; preserva referencias de filas sin cambios
  const listaConFlags = useMemo(() => {
    if (restringidosSet.size === 0 && topSet.size === 0) return lista;
    return lista.map(r => {
      const key = String(r.numero ?? r.num1 ?? '');
      const _restringido = key !== '' && restringidosSet.has(key);
      const _top = key !== '' && !_restringido && topSet.has(key);
      // Preservar referencia si los flags no cambiaron (mantiene React.memo en RowItem)
      if (Boolean(r._restringido) === _restringido && Boolean(r._top) === _top) return r;
      return { ...r, _restringido, _top };
    });
  }, [lista, restringidosSet, topSet]);

  return (
    <div style={{ padding: '8px', fontFamily: 'monospace', overflowX: 'hidden' }}>
      <h1 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 'bold' }}>LISTAS</h1>

      {/* Selector de sorteo */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ marginRight: '8px', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>Sorteo:</label>
        <select
          value={selectedSorteo?.id || ''}
          onChange={e => {
            const s = sorteos.find(x => x.id === e.target.value);
            if (s) setSelectedSorteo(s);
          }}
          style={{
            padding: '10px',
            fontFamily: 'monospace',
            fontSize: '14px',
            border: '1px solid #ccc',
            width: '100%',
            maxWidth: '480px',
            boxSizing: 'border-box',
          }}
        >
          {sorteos.map(s => (
            <option key={s.id} value={s.id}>
              {sorteoLabel(s)}
            </option>
          ))}
        </select>

        {selectedSorteo && (
          <span style={{ display: 'inline-block', marginTop: '4px', color: '#666' }}>
            Tipo: {selectedSorteo.tipo_id}
          </span>
        )}
      </div>

      {selectedSorteo && (
        (selectedSorteo.tipo_id === 'NORMAL' || selectedSorteo.tipo_id === 'REVENTADO') ? (
          <NumerosGrid
            rows={listaConFlags}
            tipo={selectedSorteo.tipo_id as TipoNumerosGrid}
          />
        ) : (
          <>
            {loadingInicial && <div>Cargando...</div>}
            {!loadingInicial && (
              <DynamicListTable
                columns={columns}
                rows={listaConFlags}
                onSave={handleSave}
                idField="id"
              />
            )}
          </>
        )
      )}


    </div>
  );
};
